/**
 * Detect and extract potential citations from document content
 */

export interface DetectedCitation {
  id: string;
  text: string;
  type: "author-year" | "numbered" | "url" | "footnote" | "unknown";
  confidence: number;
  context: string;
  lineNumber: number;
}

/**
 * Detect citations in text content using various patterns
 */
export function detectCitations(content: string): DetectedCitation[] {
  const citations: DetectedCitation[] = [];
  const lines = content.split("\n");
  let citationIndex = 0;

  lines.forEach((line, lineNum) => {
    // Skip empty lines
    if (!line.trim()) return;

    // Pattern 1: Author-Year citations (Smith, 2020) or Smith (2020)
    const authorYearPattern = /([A-Z][a-z]+(?:\s+(?:and|&)\s+[A-Z][a-z]+)*)\s*\((\d{4})\)/g;
    let match;
    
    while ((match = authorYearPattern.exec(line)) !== null) {
      citationIndex++;
      citations.push({
        id: `citation-${citationIndex}`,
        text: match[0],
        type: "author-year",
        confidence: 0.95,
        context: line.substring(Math.max(0, match.index - 50), Math.min(line.length, match.index + match[0].length + 50)),
        lineNumber: lineNum + 1,
      });
    }

    // Pattern 2: Numbered citations [1], [2], etc.
    const numberedPattern = /\[(\d+)\]/g;
    
    while ((match = numberedPattern.exec(line)) !== null) {
      // Check if this looks like a citation (not just a number in brackets)
      const beforeText = line.substring(Math.max(0, match.index - 30), match.index).trim();
      if (beforeText && !beforeText.endsWith("Figure") && !beforeText.endsWith("Table") && !beforeText.endsWith("Section")) {
        citationIndex++;
        citations.push({
          id: `citation-${citationIndex}`,
          text: match[0],
          type: "numbered",
          confidence: 0.8,
          context: line.substring(Math.max(0, match.index - 50), Math.min(line.length, match.index + match[0].length + 50)),
          lineNumber: lineNum + 1,
        });
      }
    }

    // Pattern 3: URLs
    const urlPattern = /https?:\/\/[^\s]+|www\.[^\s]+/g;
    
    while ((match = urlPattern.exec(line)) !== null) {
      citationIndex++;
      citations.push({
        id: `citation-${citationIndex}`,
        text: match[0],
        type: "url",
        confidence: 0.9,
        context: line.substring(Math.max(0, match.index - 50), Math.min(line.length, match.index + match[0].length + 50)),
        lineNumber: lineNum + 1,
      });
    }

    // Pattern 4: Footnote markers (superscript-like)
    const footnotePattern = /[a-zA-Z0-9]+\s*\^(\d+)|Ibid\.|et al\.|pp\.|pp\./g;
    
    while ((match = footnotePattern.exec(line)) !== null) {
      citationIndex++;
      citations.push({
        id: `citation-${citationIndex}`,
        text: match[0],
        type: "footnote",
        confidence: 0.75,
        context: line.substring(Math.max(0, match.index - 50), Math.min(line.length, match.index + match[0].length + 50)),
        lineNumber: lineNum + 1,
      });
    }

    // Pattern 5: Common publication indicators
    const pubPattern = /(?:Journal of|Review of|International Journal|Conference|Proceedings of|Report by|Published by|White Paper|Technical Report)/gi;
    
    while ((match = pubPattern.exec(line)) !== null) {
      citationIndex++;
      citations.push({
        id: `citation-${citationIndex}`,
        text: match[0],
        type: "unknown",
        confidence: 0.6,
        context: line.substring(Math.max(0, match.index - 50), Math.min(line.length, match.index + match[0].length + 100)),
        lineNumber: lineNum + 1,
      });
    }
  });

  // Remove duplicates and sort by line number
  const uniqueCitations = Array.from(
    new Map(citations.map(c => [c.text + c.lineNumber, c])).values()
  );

  return uniqueCitations.sort((a, b) => a.lineNumber - b.lineNumber);
}

/**
 * Extract key phrases and potential references
 */
export function extractKeyPhrases(content: string): string[] {
  const phrases: Set<string> = new Set();
  
  // Split into sentences
  const sentenceMatches = content.match(/[^.!?]+[.!?]+/g);
  const sentences: string[] = sentenceMatches || [];
  
  sentences.forEach(sentence => {
    // Extract capitalized phrases (potential proper nouns, concepts)
    const capitalizedPattern = /(?:[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g;
    const matches: string[] = (sentence as string).match(capitalizedPattern) || [];
    
    matches.forEach(match => {
      if (match.length > 3 && match.split(" ").length <= 4) {
        // Filter out common words
        const commonWords = ["The", "A", "An", "And", "Or", "But", "In", "On", "At", "To", "For"];
        if (!commonWords.includes(match)) {
          phrases.add(match);
        }
      }
    });
  });

  // Also extract noun phrases (simple heuristic)
  const nounPattern = /\b[A-Z][a-z]+\s+(?:of\s+)?[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/g;
  const nounMatches = content.match(nounPattern) || [];
  nounMatches.forEach(phrase => phrases.add(phrase));

  return Array.from(phrases).sort();
}

/**
 * Analyze citation patterns to estimate document type
 */
export function analyzeCitationPatterns(citations: DetectedCitation[]): {
  authorYearCount: number;
  numberedCount: number;
  urlCount: number;
  footnoteCount: number;
  dominantStyle: "author-year" | "numbered" | "mixed";
} {
  const counts = {
    authorYearCount: citations.filter(c => c.type === "author-year").length,
    numberedCount: citations.filter(c => c.type === "numbered").length,
    urlCount: citations.filter(c => c.type === "url").length,
    footnoteCount: citations.filter(c => c.type === "footnote").length,
  };

  let dominantStyle: "author-year" | "numbered" | "mixed" = "mixed";
  if (counts.authorYearCount > counts.numberedCount * 2) {
    dominantStyle = "author-year";
  } else if (counts.numberedCount > counts.authorYearCount * 2) {
    dominantStyle = "numbered";
  }

  return { ...counts, dominantStyle };
}

/**
 * Estimate reading level based on content complexity
 */
export function estimateReadingLevel(content: string): {
  level: "beginner" | "intermediate" | "advanced";
  complexity: number;
} {
  const sentences = content.split(/[.!?]+/).filter(s => s.trim());
  const words = content.split(/\s+/).filter(w => w.length > 0);
  
  if (sentences.length === 0 || words.length === 0) {
    return { level: "beginner", complexity: 0 };
  }

  // Calculate average sentence length
  const avgSentenceLength = words.length / sentences.length;

  // Count technical terms (rough heuristic: long words)
  const technicalWords = words.filter(w => w.length > 10).length;
  const technicalRatio = technicalWords / words.length;

  // Calculate complexity score (0-100)
  const complexityScore = Math.min(100, (avgSentenceLength * 5) + (technicalRatio * 50));

  let level: "beginner" | "intermediate" | "advanced";
  if (complexityScore < 30) {
    level = "beginner";
  } else if (complexityScore < 60) {
    level = "intermediate";
  } else {
    level = "advanced";
  }

  return {
    level,
    complexity: Math.round(complexityScore),
  };
}
